/**
 * Comprehensive SDK Test Page
 * Tests all available APIs from the SDK:
 * - ExchangeClient (trading operations)
 * - InfoClient (market data)
 * - Utility functions (satoshi conversion)
 */

import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  ExchangeClient,
  InfoClient,
  toSatoshi,
  fromSatoshi,
  toSatoshiUint64,
  isValidSatoshi,
  toSatoshiBatch,
  fromSatoshiBatch,
  SATOSHI_SCALE,
  mormSigner,
} from "../sdk";
import { useMarkets } from "../hooks/useMarkets";
import { useOnboard } from "../hooks/useOnboard";
import { useBuckets } from "../hooks/useBuckets";
import { DEFAULT_BASE_URL, DEFAULT_DEVICE_ID } from "../configs/base";
import {
  RunAllTestsButton,
  OnboardButton,
  ClearResultsButton,
  DismissErrorButton,
} from "./sdk_test_page/SDKTestPageButtons";
import { TestResultItem, TestResult } from "./sdk_test_page/TestResultItem";
import { SectionHeader } from "./sdk_test_page/SectionHeader";
import "./sdk_test_page/sdktest.css";

interface TestSection {
  name: string;
  results: TestResult[];
}

export default function SDKTestPage() {
  const [baseURL, setBaseURL] = useState(DEFAULT_BASE_URL);
  const [marketId, setMarketId] = useState<string>("");
  const [sessionToken, setSessionToken] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [deviceID, setDeviceID] = useState(DEFAULT_DEVICE_ID);

  const [sections, setSections] = useState<Record<string, TestSection>>({});
  const [isRunning, setIsRunning] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );

  const exchangeClientRef = useRef<ExchangeClient | null>(null);
  const infoClientRef = useRef<InfoClient | null>(null);
  const baseURLRef = useRef<string>(baseURL);
  const fetchedMarketsRef = useRef<any[]>([]); // Store fetched markets immediately
  const fetchedMarketIdRef = useRef<string>(""); // Store fetched market ID immediately

  // Use markets hook to manage market data retention
  const { markets, fetchMarkets } = useMarkets({
    baseURL,
    sessionToken,
    autoFetchMarkets: false, // We'll fetch manually in testInfoClient
  });

  // Use onboarding hook as single source of truth for account information
  const {
    mnemonic,
    walletAddress,
    authToken: onboardAuthToken,
    loading: onboardLoading,
    error: onboardError,
    onboard: performOnboard,
    clearError: clearOnboardError,
    setMnemonic: setOnboardMnemonic,
  } = useOnboard(baseURL);

  // Initialize mnemonic with default value if not set
  useEffect(() => {
    if (!mnemonic) {
      setOnboardMnemonic(
        "legal winner thank year wave sausage worth useful legal winner thank yellow",
      );
    }
  }, [mnemonic, setOnboardMnemonic]);

  // Update mnemonic ref when mnemonic changes
  const mnemonicRef = useRef<string>(mnemonic || "");
  useEffect(() => {
    mnemonicRef.current = mnemonic || "";
  }, [mnemonic]);

  // Use buckets hook to manage bucket data
  const {
    buckets: hookBuckets,
    loading: bucketsLoading,
    error: bucketsError,
    refresh: refreshBuckets,
  } = useBuckets({
    baseURL,
    authToken: sessionToken || authToken,
    walletAddress: walletAddress || undefined,
    refreshInterval: 0, // Disable auto-refresh for test page
  });

  // Sync onboard auth token to local state when it changes
  useEffect(() => {
    if (onboardAuthToken) {
      setAuthToken(onboardAuthToken);
      // Store in localStorage for sharing with other pages
      localStorage.setItem("authToken", onboardAuthToken);
    }
  }, [onboardAuthToken]);

  // Sync authToken state to localStorage whenever it changes
  useEffect(() => {
    if (authToken && authToken.trim() !== "") {
      localStorage.setItem("authToken", authToken);
    }
  }, [authToken]);

  // Show test completion toast when tests finish
  const prevIsRunningRef = useRef(isRunning);
  useEffect(() => {
    // When isRunning changes from true to false, tests just completed
    if (prevIsRunningRef.current === true && isRunning === false) {
      // Wait a bit for state to update, then calculate results
      setTimeout(() => {
        const allResults = Object.values(sections).flatMap((s) => s.results);
        const successCount = allResults.filter((r) => r.success).length;
        const totalCount = allResults.length;
        const failureCount = totalCount - successCount;

        if (totalCount > 0) {
          if (failureCount === 0) {
            toast.success("All tests passed", {
              description: `${totalCount} test(s) completed successfully.`,
              duration: 5000,
            });
          } else {
            toast.warning("Tests completed with failures", {
              description: `${successCount}/${totalCount} tests passed. ${failureCount} test(s) failed.`,
              duration: 6000,
            });
          }
        }
      }, 100);
    }
    prevIsRunningRef.current = isRunning;
  }, [isRunning, sections]);

  // Load authToken from localStorage on mount
  useEffect(() => {
    const storedAuthToken = localStorage.getItem("authToken");
    if (storedAuthToken && !authToken) {
      setAuthToken(storedAuthToken);
    }
  }, []);

  // Compute default market ID (first active market, or first in list)
  // Use market_id (string) from API, fallback to id (number) converted to string
  const defaultMarketId =
    markets.length > 0
      ? (markets.find((m) => m.active)?.market_id ?? markets[0].market_id) ||
        (markets.find((m) => m.active)?.id?.toString() ??
          markets[0].id?.toString() ??
          "")
      : null;

  // Use defaultMarketId if available, fallback to user input
  const effectiveMarketId = defaultMarketId ?? marketId;

  // Update marketId state when default changes (for UI consistency)
  useEffect(() => {
    if (defaultMarketId && marketId !== defaultMarketId) {
      setMarketId(defaultMarketId);
    }
  }, [defaultMarketId]);

  // Update refs when values change
  useEffect(() => {
    baseURLRef.current = baseURL;
  }, [baseURL]);

  const addResult = (section: string, result: TestResult) => {
    setSections((prev) => {
      const sectionData = prev[section] || { name: section, results: [] };
      return {
        ...prev,
        [section]: {
          ...sectionData,
          results: [...sectionData.results, result],
        },
      };
    });
  };

  const clearResults = () => {
    setSections({});
    setExpandedSections(new Set());
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionName)) {
        next.delete(sectionName);
      } else {
        next.add(sectionName);
      }
      return next;
    });
  };

  // Auto-expand sections when they first get results
  useEffect(() => {
    const newSections = Object.keys(sections).filter(
      (name) => sections[name].results.length > 0,
    );
    if (newSections.length > 0) {
      setExpandedSections((prev) => {
        const next = new Set(prev);
        newSections.forEach((name) => next.add(name));
        return next;
      });
    }
  }, [sections]);

  // Utility Functions Tests
  const testUtilities = () => {
    const section = "Utilities";
    addResult(section, {
      success: true,
      message: "Testing utility functions...",
      timestamp: Date.now(),
    });

    try {
      // Test toSatoshi
      const satoshi1 = toSatoshi(1.0);
      addResult(section, {
        success: satoshi1 === "100000000",
        message: `toSatoshi(1.0) = ${satoshi1}`,
        data: { expected: "100000000", got: satoshi1 },
        timestamp: Date.now(),
      });

      const satoshi2 = toSatoshi("1.5");
      addResult(section, {
        success: satoshi2 === "150000000",
        message: `toSatoshi("1.5") = ${satoshi2}`,
        data: { expected: "150000000", got: satoshi2 },
        timestamp: Date.now(),
      });

      // Test fromSatoshi
      const decimal1 = fromSatoshi("100000000");
      addResult(section, {
        success: decimal1 === 1.0,
        message: `fromSatoshi("100000000") = ${decimal1}`,
        data: { expected: 1.0, got: decimal1 },
        timestamp: Date.now(),
      });

      // Test toSatoshiUint64
      const uint64 = toSatoshiUint64(1.0);
      addResult(section, {
        success: uint64 === 100000000,
        message: `toSatoshiUint64(1.0) = ${uint64}`,
        data: { expected: 100000000, got: uint64 },
        timestamp: Date.now(),
      });

      // Test isValidSatoshi
      const valid1 = isValidSatoshi("100000000");
      const valid2 = isValidSatoshi("abc");
      addResult(section, {
        success: valid1 && !valid2,
        message: `isValidSatoshi tests: "100000000"=${valid1}, "abc"=${valid2}`,
        data: { valid1, valid2 },
        timestamp: Date.now(),
      });

      // Test batch functions
      const batchInput = [1.0, 1.5, 2.0];
      const batchSatoshi = toSatoshiBatch(batchInput);
      const batchDecimal = fromSatoshiBatch(batchSatoshi);
      addResult(section, {
        success: batchDecimal.length === 3,
        message: `Batch conversion: ${batchInput.length} items`,
        data: {
          input: batchInput,
          satoshi: batchSatoshi,
          decimal: batchDecimal,
        },
        timestamp: Date.now(),
      });

      // Test SATOSHI_SCALE constant
      addResult(section, {
        success: SATOSHI_SCALE === 100000000,
        message: `SATOSHI_SCALE = ${SATOSHI_SCALE}`,
        data: { expected: 100000000, got: SATOSHI_SCALE },
        timestamp: Date.now(),
      });
    } catch (error: any) {
      addResult(section, {
        success: false,
        message: "Utility test error",
        error: error.message,
        timestamp: Date.now(),
      });
    }
  };

  // Authentication Test Section
  const testAuthentication = async (section: string = "Authentication") => {
    if (!walletAddress || walletAddress.trim() === "") {
      addResult(section, {
        success: false,
        message: "Authentication skipped: Wallet address required",
        timestamp: Date.now(),
      });
      return;
    }

    // Check if mormSigner is available for address generation (still needed)
    // Note: Actual signing now uses ethers.js, but we need WASM for address derivation
    if (!mormSigner.isReady()) {
      addResult(section, {
        success: false,
        message:
          "Authentication failed: mormSigner not loaded. Please initialize mormSigner first for address generation.",
        timestamp: Date.now(),
      });
      return;
    }

    try {
      addResult(section, {
        success: true,
        message:
          "Starting authentication (onboarding) with ethers.js EIP-712 signing...",
        timestamp: Date.now(),
      });

      // Use the refactored onboarding function
      // This uses ethers.js for EIP-712 signing (via signing/onboard.ts)
      // onboardAndGetAuthToken handles: nonce fetching, message building, signing, and HTTP request
      const { onboardAndGetAuthToken } = await import("../sdk/utils/onboard");

      addResult(section, {
        success: true,
        message:
          "Calling onboardAndGetAuthToken (handles nonce, EIP-712 signing with ethers.js, and API request)...",
        timestamp: Date.now(),
      });

      if (!mnemonic) {
        throw new Error("Mnemonic is required for authentication");
      }

      const result = await onboardAndGetAuthToken(mnemonic, baseURLRef.current);

      if (!result.success || !result.authToken) {
        throw new Error(result.error || "Onboarding failed");
      }

      // Success! Store token (the hook's useEffect will sync onboardAuthToken)
      setAuthToken(result.authToken);
      // Store in localStorage for sharing with other pages
      localStorage.setItem("authToken", result.authToken);

      addResult(section, {
        success: true,
        message: `AUTHENTICATION SUCCESSFUL!`,
        data: {
          walletAddress,
          authToken: result.authToken.substring(0, 32) + "...",
          refreshToken: result.refreshToken,
          expiresAt: result.expiresAt,
        },
        timestamp: Date.now(),
      });
    } catch (error: any) {
      const msg = error.message || String(error);
      addResult(section, {
        success: false,
        message: `Authentication failed: ${msg}`,
        error: msg,
        timestamp: Date.now(),
      });
    }
  };
  // Wallet Specific Tests (requires wallet address, session token is optional)
  const testWalletSpecific = async () => {
    const section = "Wallet Specific";

    // Recreate client with current baseURL
    infoClientRef.current = new InfoClient({ baseURL: baseURLRef.current });

    // Always update session token if provided
    if (sessionToken && infoClientRef.current) {
      infoClientRef.current.setSessionToken(sessionToken);
    }

    // Wait for wallet address to be available (with timeout)
    addResult(section, {
      success: true,
      message: "Waiting for wallet address to be available...",
      timestamp: Date.now(),
    });

    const maxWaitTime = 5000; // 5 seconds timeout
    const pollInterval = 200; // Check every 200ms
    let waitedTime = 0;

    // Wait for wallet address to be available
    while (!walletAddress || walletAddress.trim() === "") {
      if (waitedTime >= maxWaitTime) {
        addResult(section, {
          success: false,
          message: "Timeout: Wallet address not available after 5 seconds",
          error:
            "Wallet address is required for wallet-specific tests. Please ensure mnemonic is provided.",
          timestamp: Date.now(),
        });
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
      waitedTime += pollInterval;
      // Note: walletAddress is a closure variable that captures the state value
      // If the state updates during this wait, the closure won't reflect it
      // However, the wallet address is typically generated synchronously from mnemonic
      // So this wait is mainly for cases where generation is still in progress
    }

    if (!walletAddress || walletAddress.trim() === "") {
      addResult(section, {
        success: false,
        message: "Wallet address is required for wallet-specific tests",
        error: "Wallet address not available",
        timestamp: Date.now(),
      });
      return;
    }

    addResult(section, {
      success: true,
      message: `Wallet address available: ${walletAddress.substring(0, 10)}...`,
      timestamp: Date.now(),
    });

    try {
      // Test getBalance
      addResult(section, {
        success: true,
        message: `Testing getBalance() for asset "MORM" at address ${walletAddress.substring(0, 10)}...`,
        timestamp: Date.now(),
      });
      try {
        const balance = await infoClientRef.current.getBalance(
          walletAddress,
          "MORM",
        );
        const balanceValue =
          balance?.balance || balance?.amount || balance?.value || "N/A";
        const balanceStr =
          typeof balanceValue === "string"
            ? balanceValue
            : JSON.stringify(balanceValue);
        addResult(section, {
          success: !!balance,
          message: `getBalance() - GET /bank/v1/balance/{address} | Asset: MORM | Balance: ${balanceStr} (raw satoshi format)`,
          data: {
            balance,
            asset: "MORM",
            walletAddress: walletAddress.substring(0, 10) + "...",
            note: "Balance is returned in satoshi format (raw integer string). Use getBalanceDecimal() for decimal format.",
          },
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: `getBalance() failed for asset "MORM" (may require authentication)`,
          error: error.message,
          data: {
            asset: "MORM",
            walletAddress: walletAddress.substring(0, 10) + "...",
            endpoint: "GET /bank/v1/balance/{address}",
          },
          timestamp: Date.now(),
        });
      }

      // Test getBalance for USDC
      addResult(section, {
        success: true,
        message: `Testing getBalance() for asset "USDC" at address ${walletAddress.substring(0, 10)}...`,
        timestamp: Date.now(),
      });
      try {
        const balanceUSDC = await infoClientRef.current.getBalance(
          walletAddress,
          "USDC",
        );
        const balanceValueUSDC =
          balanceUSDC?.balance ||
          balanceUSDC?.amount ||
          balanceUSDC?.value ||
          "N/A";
        const balanceStrUSDC =
          typeof balanceValueUSDC === "string"
            ? balanceValueUSDC
            : JSON.stringify(balanceValueUSDC);
        addResult(section, {
          success: !!balanceUSDC,
          message: `getBalance() - GET /bank/v1/balance/{address} | Asset: USDC | Balance: ${balanceStrUSDC} (raw satoshi format)`,
          data: {
            balance: balanceUSDC,
            asset: "USDC",
            walletAddress: walletAddress.substring(0, 10) + "...",
            note: "Balance is returned in satoshi format (raw integer string). Use getBalanceDecimal() for decimal format.",
          },
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: `getBalance() failed for asset "USDC" (may require authentication)`,
          error: error.message,
          data: {
            asset: "USDC",
            walletAddress: walletAddress.substring(0, 10) + "...",
            endpoint: "GET /bank/v1/balance/{address}",
          },
          timestamp: Date.now(),
        });
      }

      // Test getBalanceDecimal
      try {
        const balanceDecimal = await infoClientRef.current.getBalanceDecimal(
          walletAddress,
          "USDC",
        );
        addResult(section, {
          success: !!balanceDecimal,
          message: "getBalanceDecimal() - GET /bank/v1/balance/{address}",
          data: balanceDecimal,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getBalanceDecimal() failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getBalances
      try {
        const balances = await infoClientRef.current.getBalances(walletAddress);
        addResult(section, {
          success: !!balances,
          message: "getBalances() - GET /bank/v1/balances/{address}",
          data: balances,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getBalances() failed (may require authentication)",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getPositions
      try {
        const positions =
          await infoClientRef.current.getPositions(walletAddress);
        addResult(section, {
          success: Array.isArray(positions),
          message: `getPositions() - GET /bucket/v1/address/{address}/positions returned ${Array.isArray(positions) ? positions.length : 0} positions`,
          data: positions,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getPositions() failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getPositionsDecimal
      try {
        const positionsDecimal =
          await infoClientRef.current.getPositionsDecimal(walletAddress);
        addResult(section, {
          success: Array.isArray(positionsDecimal),
          message: `getPositionsDecimal() - GET /bucket/v1/address/{address}/positions returned ${Array.isArray(positionsDecimal) ? positionsDecimal.length : 0} positions`,
          data: positionsDecimal,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getPositionsDecimal() failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getOrders
      let ordersArray: any[] = [];
      try {
        const ordersResponse =
          await infoClientRef.current.getOrders(walletAddress);
        // Response structure: { orders: Order[], total_count: number, timestamp: string }
        ordersArray = ordersResponse?.orders || [];
        const isValidResponse =
          ordersResponse &&
          (Array.isArray(ordersArray) ||
            ordersResponse.total_count !== undefined);
        addResult(section, {
          success: isValidResponse,
          message: `getOrders() - GET /clob/v1/address/orders?address={address} returned ${Array.isArray(ordersArray) ? ordersArray.length : 0} orders (total_count: ${ordersResponse?.total_count ?? "N/A"})`,
          data: {
            orders: Array.isArray(ordersArray)
              ? ordersArray.slice(0, 3)
              : ordersArray,
            total_count: ordersResponse?.total_count,
            timestamp: ordersResponse?.timestamp,
          },
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getOrders() failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getOrder by ID (only if getOrders returned at least one order)
      if (Array.isArray(ordersArray) && ordersArray.length > 0) {
        const firstOrder = ordersArray[0];
        const orderId =
          firstOrder?.orderId || firstOrder?.order_id || firstOrder?.id;
        if (orderId) {
          addResult(section, {
            success: true,
            message: `Testing getOrder() for order ID: ${orderId.substring(0, 16)}... (from getOrders result)`,
            timestamp: Date.now(),
          });
          try {
            const orderById = await infoClientRef.current.getOrder(orderId);
            addResult(section, {
              success: !!orderById,
              message: `getOrder() - GET /clob/v1/order/{orderID} | Order ID: ${orderId.substring(0, 16)}...`,
              data: {
                order: orderById,
                orderId: orderId,
                source: "First order from getOrders() result",
              },
              timestamp: Date.now(),
            });
          } catch (error: any) {
            addResult(section, {
              success: false,
              message: `getOrder() failed for order ID: ${orderId.substring(0, 16)}...`,
              error: error.message,
              data: {
                orderId: orderId,
                endpoint: "GET /clob/v1/order/{orderID}",
              },
              timestamp: Date.now(),
            });
          }
        } else {
          addResult(section, {
            success: false,
            message:
              "getOrder() test skipped: No valid order ID found in getOrders() result",
            error: "Order ID not found in order object",
            data: {
              order: firstOrder,
              availableFields: Object.keys(firstOrder || {}),
            },
            timestamp: Date.now(),
          });
        }
      } else {
        addResult(section, {
          success: true,
          message:
            "getOrder() test skipped: No orders available from getOrders() (need at least 1 order)",
          data: {
            ordersCount: ordersArray.length,
            note: "getOrder() test requires at least one order from getOrders()",
          },
          timestamp: Date.now(),
        });
      }

      // Test getOrderHistory
      try {
        const orderHistoryResponse =
          await infoClientRef.current.getOrderHistory(walletAddress);
        // Handle both array response and object response with orders array
        const orderHistoryArray = Array.isArray(orderHistoryResponse)
          ? orderHistoryResponse
          : orderHistoryResponse?.orders || [];
        const isValidResponse =
          orderHistoryResponse &&
          (Array.isArray(orderHistoryArray) ||
            orderHistoryResponse.orders !== undefined);
        addResult(section, {
          success: isValidResponse,
          message: `getOrderHistory() - GET /clob/v1/address/orders/history?address={address} returned ${Array.isArray(orderHistoryArray) ? orderHistoryArray.length : 0} orders`,
          data: Array.isArray(orderHistoryArray)
            ? orderHistoryArray.slice(0, 3)
            : orderHistoryResponse,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getOrderHistory() failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getTradeHistory
      try {
        const tradeHistoryResponse =
          await infoClientRef.current.getTradeHistory(walletAddress);
        // Handle both array response and object response with trades array
        const tradeHistoryArray = Array.isArray(tradeHistoryResponse)
          ? tradeHistoryResponse
          : tradeHistoryResponse?.trades || tradeHistoryResponse?.data || [];
        const isValidResponse =
          tradeHistoryResponse &&
          (Array.isArray(tradeHistoryArray) ||
            tradeHistoryResponse.trades !== undefined ||
            tradeHistoryResponse.data !== undefined);
        addResult(section, {
          success: isValidResponse,
          message: `getTradeHistory() - GET /clob/v1/address/trades?address={address} returned ${Array.isArray(tradeHistoryArray) ? tradeHistoryArray.length : 0} trades`,
          data: Array.isArray(tradeHistoryArray)
            ? tradeHistoryArray.slice(0, 3)
            : tradeHistoryResponse,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getTradeHistory() failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getBuckets (direct SDK call)
      try {
        const buckets = await infoClientRef.current.getBuckets(walletAddress);
        addResult(section, {
          success: Array.isArray(buckets) || buckets !== undefined,
          message: `getBuckets() - GET /bucket/v1/address/{address}/buckets returned ${Array.isArray(buckets) ? buckets.length : "data"}`,
          data: Array.isArray(buckets) ? buckets.slice(0, 3) : buckets,
          timestamp: Date.now(),
        });

        // If buckets array exists and has items, test getBucket with first bucket ID
        if (Array.isArray(buckets) && buckets.length > 0) {
          const firstBucket = buckets[0];
          const bucketId =
            firstBucket.bucket_id || firstBucket.id || firstBucket.bucketId;
          if (bucketId) {
            try {
              const bucket = await infoClientRef.current.getBucket(bucketId);
              addResult(section, {
                success: !!bucket,
                message: `getBucket() - GET /bucket/v1/bucket/{bucket_id} (${bucketId.substring(0, 16)}...)`,
                data: bucket,
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: `getBucket(${bucketId.substring(0, 16)}...) failed`,
                error: error.message,
                timestamp: Date.now(),
              });
            }
          }
        }
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getBuckets() failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test useBuckets hook
      addResult(section, {
        success: true,
        message: "Testing useBuckets hook...",
        timestamp: Date.now(),
      });
      try {
        // Refresh buckets using the hook
        await refreshBuckets();
        addResult(section, {
          success: !bucketsError && !bucketsLoading,
          message: `useBuckets hook: ${hookBuckets.length} buckets loaded`,
          data: {
            buckets: hookBuckets.slice(0, 3),
            loading: bucketsLoading,
            error: bucketsError,
            totalBuckets: hookBuckets.length,
          },
          timestamp: Date.now(),
        });

        // If hook has buckets, test getBucket with first one
        if (hookBuckets.length > 0) {
          const firstHookBucket = hookBuckets[0];
          const hookBucketId = firstHookBucket.bucketId?.toString();
          if (hookBucketId) {
            try {
              const bucket =
                await infoClientRef.current.getBucket(hookBucketId);
              addResult(section, {
                success: !!bucket,
                message: `getBucket() - GET /bucket/v1/bucket/{bucket_id} via hook bucket ID (${hookBucketId.substring(0, 16)}...)`,
                data: bucket,
                timestamp: Date.now(),
              });
            } catch (error: any) {
              addResult(section, {
                success: false,
                message: `getBucket() via hook bucket ID failed`,
                error: error.message,
                timestamp: Date.now(),
              });
            }
          }
        }
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "useBuckets hook test failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }
    } catch (error: any) {
      addResult(section, {
        success: false,
        message: "Wallet Specific test error",
        error: error.message,
        timestamp: Date.now(),
      });
    }
  };

  const testInfoClient = async () => {
    const section = "InfoClient";

    // Recreate client with current baseURL
    infoClientRef.current = new InfoClient({ baseURL: baseURLRef.current });

    // Always update session token if provided
    if (sessionToken && infoClientRef.current) {
      infoClientRef.current.setSessionToken(sessionToken);
    }

    try {
      // Test getMarkets - use hook to retain data
      addResult(section, {
        success: true,
        message: "Testing getMarkets()...",
        timestamp: Date.now(),
      });

      // Fetch markets using the hook to retain data in store
      const marketsData = await fetchMarkets();
      // Store in ref immediately so it's available to other test functions
      fetchedMarketsRef.current = Array.isArray(marketsData) ? marketsData : [];
      const marketCount = Array.isArray(marketsData) ? marketsData.length : 0;

      // Find first active market, or first market in list
      const firstMarket =
        marketCount > 0
          ? (marketsData.find((m: any) => m.active || m.status === "active") ??
            marketsData[0])
          : null;

      // Use exact market_id (string) from API, fallback to id converted to string
      const firstMarketId =
        firstMarket?.market_id ?? firstMarket?.id?.toString() ?? null;

      addResult(section, {
        success: Array.isArray(marketsData) && marketCount > 0,
        message: `getMarkets() - GET /market/v1/all returned ${marketCount} markets${firstMarketId ? ` | Using market ID: ${firstMarketId} (${firstMarket?.moniker || firstMarket?.symbol || "auto-selected"})` : ""}`,
        data: {
          count: marketCount,
          defaultMarketId: firstMarketId,
          defaultMarket: firstMarket
            ? {
                id: firstMarket.id,
                market_id: firstMarket.market_id,
                moniker: firstMarket.moniker,
                symbol: firstMarket.symbol,
                active: firstMarket.active,
                status: firstMarket.status,
              }
            : null,
          markets: marketsData.slice(0, 5), // Show first 5
        },
        timestamp: Date.now(),
      });

      // Automatically set the first market ID for demo purposes (use exact string)
      if (firstMarketId) {
        setMarketId(firstMarketId);
        // Store in ref immediately so it's available to other test functions
        fetchedMarketIdRef.current = firstMarketId;

        const marketDisplayName =
          firstMarket?.moniker ||
          firstMarket?.symbol ||
          (firstMarket?.baseAsset && firstMarket?.quoteAsset
            ? `${firstMarket.baseAsset}-${firstMarket.quoteAsset}`
            : "Unknown");

        addResult(section, {
          success: true,
          message: `Markets loaded: ${marketCount} | Auto-selected: ${marketDisplayName} (ID: ${firstMarketId.substring(0, 10)}...)`,
          data: {
            selectedMarketId: firstMarketId,
            count: marketCount,
            market: firstMarket,
          },
          timestamp: Date.now(),
        });

        // Critical: Wait for React state to propagate before next tests
        addResult(section, {
          success: true,
          message: `Waiting 1.5s for market selection to take effect...`,
          timestamp: Date.now(),
        });

        await new Promise((resolve) => setTimeout(resolve, 1500)); // 1.5 second delay

        addResult(section, {
          success: true,
          message: `Market ID ready: ${firstMarketId}`,
          data: { marketId: firstMarketId },
          timestamp: Date.now(),
        });

        // Optional: Confirm in UI
        addResult(section, {
          success: true,
          message: `Proceeding with market: ${marketDisplayName}`,
          timestamp: Date.now(),
        });
      }

      // Test market-specific endpoints (only if market ID is available)
      // After the delay, React state should be updated, but use the ID we just set to be safe
      const currentMarketId = firstMarketId || effectiveMarketId || marketId;
      if (currentMarketId) {
        // Test getOrderbook (with decimal conversion)
        addResult(section, {
          success: true,
          message: `Testing getOrderbook(${currentMarketId}, true)...`,
          timestamp: Date.now(),
        });
        try {
          const orderbook = await infoClientRef.current.getOrderbook(
            currentMarketId,
            true,
          );
          addResult(section, {
            success: orderbook && (orderbook.bids || orderbook.asks),
            message:
              "getOrderbook() - GET /clob/v1/orderbook/{marketId} with decimal conversion",
            data: {
              bids: orderbook?.bids?.slice(0, 3),
              asks: orderbook?.asks?.slice(0, 3),
            },
            timestamp: Date.now(),
          });

          // Test getOrderbook (raw satoshi)
          const orderbookRaw = await infoClientRef.current.getOrderbook(
            currentMarketId,
            false,
          );
          addResult(section, {
            success: orderbookRaw && (orderbookRaw.bids || orderbookRaw.asks),
            message:
              "getOrderbook() - GET /clob/v1/orderbook/{marketId} raw satoshi format",
            data: {
              bids: orderbookRaw?.bids?.slice(0, 2),
              asks: orderbookRaw?.asks?.slice(0, 2),
            },
            timestamp: Date.now(),
          });
        } catch (error: any) {
          addResult(section, {
            success: false,
            message:
              "getOrderbook() - GET /clob/v1/orderbook/{marketId} failed",
            error: error.message,
            timestamp: Date.now(),
          });
        }

        // Test getTicker
        addResult(section, {
          success: true,
          message: `Testing getTicker(${currentMarketId})...`,
          timestamp: Date.now(),
        });
        try {
          const ticker = await infoClientRef.current.getTicker(currentMarketId);
          addResult(section, {
            success: !!ticker,
            message: "getTicker() - GET /market/v1/market/{marketId}/stats",
            data: ticker,
            timestamp: Date.now(),
          });
        } catch (error: any) {
          addResult(section, {
            success: false,
            message:
              "getTicker() - GET /market/v1/market/{marketId}/stats failed",
            error: error.message,
            timestamp: Date.now(),
          });
        }

        // Test getRecentTrades
        addResult(section, {
          success: true,
          message: `Testing getRecentTrades(${currentMarketId}, 10)...`,
          timestamp: Date.now(),
        });
        try {
          const trades = await infoClientRef.current.getRecentTrades(
            currentMarketId,
            10,
          );
          addResult(section, {
            success: Array.isArray(trades),
            message: `getRecentTrades() - GET /clob/v1/trades?market_id={marketId} returned ${Array.isArray(trades) ? trades.length : 0} trades`,
            data: trades?.slice(0, 3),
            timestamp: Date.now(),
          });
        } catch (error: any) {
          addResult(section, {
            success: false,
            message:
              "getRecentTrades() - GET /clob/v1/trades?market_id={marketId} failed",
            error: error.message,
            timestamp: Date.now(),
          });
        }
      } else {
        addResult(section, {
          success: false,
          message: "Skipping market-specific tests: No market ID available",
          error: "Please ensure markets are loaded and a market is selected",
          timestamp: Date.now(),
        });
      }

      // Test router/v1 endpoints (public, no auth required)
      addResult(section, {
        success: true,
        message: "Testing router/v1 endpoints...",
        timestamp: Date.now(),
      });

      // Test getSupportedActions
      try {
        const supportedActions =
          await infoClientRef.current.getSupportedActions();
        addResult(section, {
          success:
            Array.isArray(supportedActions?.actions) ||
            supportedActions?.actions !== undefined,
          message:
            "getSupportedActions() - GET /router/v1/tx/supported-actions",
          data: supportedActions,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message:
            "getSupportedActions() - GET /router/v1/tx/supported-actions failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test validateAction
      try {
        const validateResult =
          await infoClientRef.current.validateAction("clob::submit_order");
        addResult(section, {
          success:
            validateResult?.valid !== undefined || validateResult !== undefined,
          message:
            'validateAction() - GET /router/v1/tx/validate-action ("clob::submit_order")',
          data: validateResult,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message:
            "validateAction() - GET /router/v1/tx/validate-action failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getHealthSummary
      try {
        const healthSummary = await infoClientRef.current.getHealthSummary();
        addResult(section, {
          success: !!healthSummary,
          message: "getHealthSummary() - GET /router/v1/health/summary",
          data: healthSummary,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getHealthSummary() - GET /router/v1/health/summary failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getHealthDaemons
      try {
        const healthDaemons = await infoClientRef.current.getHealthDaemons();
        addResult(section, {
          success: !!healthDaemons,
          message: "getHealthDaemons() - GET /router/v1/health/daemons",
          data: healthDaemons,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getHealthDaemons() - GET /router/v1/health/daemons failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getHealthOracle
      try {
        const healthOracle = await infoClientRef.current.getHealthOracle();
        addResult(section, {
          success: !!healthOracle,
          message: "getHealthOracle() - GET /router/v1/health/oracle",
          data: healthOracle,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getHealthOracle() - GET /router/v1/health/oracle failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getServerTime
      try {
        const serverTime = await infoClientRef.current.getServerTime();
        addResult(section, {
          success:
            !!serverTime &&
            (serverTime.serverTime !== undefined || serverTime !== undefined),
          message: "getServerTime() - GET /router/v1/time",
          data: serverTime,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getServerTime() - GET /router/v1/time failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getMetricsTPS
      try {
        const metricsTPS = await infoClientRef.current.getMetricsTPS();
        addResult(section, {
          success: !!metricsTPS,
          message: "getMetricsTPS() - GET /router/v1/metrics/tps",
          data: metricsTPS,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "getMetricsTPS() - GET /router/v1/metrics/tps failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test getMetricsLatency
      try {
        const metricsLatency = await infoClientRef.current.getMetricsLatency();
        addResult(section, {
          success: !!metricsLatency,
          message: "getMetricsLatency() - GET /router/v1/metrics/latency",
          data: metricsLatency,
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message:
            "getMetricsLatency() - GET /router/v1/metrics/latency failed",
          error: error.message,
          timestamp: Date.now(),
        });
      }
    } catch (error: any) {
      addResult(section, {
        success: false,
        message: "InfoClient test error",
        error: error.message,
        timestamp: Date.now(),
      });
    }
  };

  // ExchangeClient Tests
  const testExchangeClient = async () => {
    const section = "ExchangeClient";
    if (!mnemonic) {
      addResult(section, {
        success: false,
        message: "Mnemonic required for ExchangeClient tests",
        error: "No mnemonic provided",
        timestamp: Date.now(),
      });
      return;
    }

    // Read market ID from page state (UI input) or from loaded markets
    // Try in order: 1) marketId from state, 2) effectiveMarketId (has fallback), 3) from ref (immediately available), 4) from markets array
    let currentMarketId =
      marketId || effectiveMarketId || fetchedMarketIdRef.current || "";

    // If still empty, try to get from fetched markets ref (loaded in previous sections, immediately available)
    if (!currentMarketId || currentMarketId.trim() === "") {
      if (fetchedMarketsRef.current && fetchedMarketsRef.current.length > 0) {
        // Find first active market, or first market in list (same logic as testInfoClient)
        const firstMarket =
          fetchedMarketsRef.current.find(
            (m: any) => m.active || m.status === "active",
          ) ?? fetchedMarketsRef.current[0];
        if (firstMarket) {
          currentMarketId =
            firstMarket.market_id ?? firstMarket.id?.toString() ?? "";
          fetchedMarketIdRef.current = currentMarketId; // Store for next time
          addResult(section, {
            success: true,
            message: `Market ID not in state, using from fetched markets ref: ${currentMarketId.substring(0, 16)}...`,
            data: {
              source: "fetchedMarketsRef",
              marketId: currentMarketId,
              market: {
                moniker: firstMarket.moniker,
                symbol: firstMarket.symbol,
                active: firstMarket.active,
              },
            },
            timestamp: Date.now(),
          });
        }
      } else if (markets && markets.length > 0) {
        // Fallback to markets state (might be updated by now)
        const firstMarket =
          markets.find((m: any) => m.active || m.status === "active") ??
          markets[0];
        if (firstMarket) {
          currentMarketId =
            firstMarket.market_id ?? firstMarket.id?.toString() ?? "";
          addResult(section, {
            success: true,
            message: `Market ID not in state, using from markets state: ${currentMarketId.substring(0, 16)}...`,
            data: {
              source: "markets state",
              marketId: currentMarketId,
              market: {
                moniker: firstMarket.moniker,
                symbol: firstMarket.symbol,
                active: firstMarket.active,
              },
            },
            timestamp: Date.now(),
          });
        }
      }
    }

    // Final check - if still empty, show error with debug info
    if (!currentMarketId || currentMarketId.trim() === "") {
      addResult(section, {
        success: false,
        message: "Market ID required from page or loaded markets",
        error: `Please enter or select a market ID in the configuration section, or ensure markets are loaded first. Debug: marketId="${marketId || "(empty)"}", effectiveMarketId="${effectiveMarketId || "(empty)"}", fetchedMarketIdRef="${fetchedMarketIdRef.current || "(empty)"}", fetchedMarketsRef.length=${fetchedMarketsRef.current.length}, markets.length=${markets.length}`,
        data: {
          marketId: marketId || "(empty)",
          effectiveMarketId: effectiveMarketId || "(empty)",
          defaultMarketId: defaultMarketId || "(empty)",
          fetchedMarketIdRef: fetchedMarketIdRef.current || "(empty)",
          fetchedMarketsRefCount: fetchedMarketsRef.current.length,
          marketsCount: markets.length,
          marketsAvailable:
            markets.length > 0 || fetchedMarketsRef.current.length > 0,
        },
        timestamp: Date.now(),
      });
      return;
    }

    // Read wallet address from page state (UI input)
    const currentWalletAddress = walletAddress || "";
    if (!currentWalletAddress || currentWalletAddress.trim() === "") {
      addResult(section, {
        success: false,
        message: "Wallet address required from page",
        error:
          "Wallet address should be generated from mnemonic or entered manually. Please ensure mnemonic is valid.",
        timestamp: Date.now(),
      });
      return;
    }

    // Determine source of market ID for logging
    const marketIdSource = marketId
      ? "page state (marketId)"
      : effectiveMarketId
        ? "page state (effectiveMarketId)"
        : "loaded markets array";

    addResult(section, {
      success: true,
      message: `Using market ID from ${marketIdSource}: ${currentMarketId.substring(0, 16)}...`,
      data: {
        marketId: currentMarketId,
        source: marketIdSource,
      },
      timestamp: Date.now(),
    });

    addResult(section, {
      success: true,
      message: `Using wallet address from page: ${currentWalletAddress.substring(0, 10)}...`,
      data: { walletAddress: currentWalletAddress },
      timestamp: Date.now(),
    });

    // Log signing tokens if available
    if (sessionToken || authToken) {
      addResult(section, {
        success: true,
        message: `Using signing tokens: ${sessionToken ? "Session Token" : ""} ${authToken ? "Auth Token" : ""}`,
        data: {
          hasSessionToken: !!sessionToken,
          hasAuthToken: !!authToken,
        },
        timestamp: Date.now(),
      });
    }

    // Recreate client with current config
    exchangeClientRef.current = new ExchangeClient({
      baseURL: baseURLRef.current,
      mnemonic: mnemonicRef.current,
    });

    try {
      // Initialize
      addResult(section, {
        success: true,
        message: "Initializing ExchangeClient...",
        timestamp: Date.now(),
      });
      await exchangeClientRef.current.initialize();
      const walletAddr = exchangeClientRef.current.getWalletAddress();

      // Verify wallet address matches the one from page
      if (walletAddr.toLowerCase() !== currentWalletAddress.toLowerCase()) {
        addResult(section, {
          success: false,
          message: "Wallet address mismatch",
          error: `ExchangeClient wallet (${walletAddr}) does not match page wallet (${currentWalletAddress})`,
          timestamp: Date.now(),
        });
        return;
      }

      addResult(section, {
        success: true,
        message: `ExchangeClient initialized successfully (Wallet: ${walletAddr.substring(0, 10)}...)`,
        data: {
          walletAddress: walletAddr,
          marketId: currentMarketId,
          verified: true,
        },
        timestamp: Date.now(),
      });

      // Use marketId as string directly (no conversion needed)
      const marketIdString = currentMarketId;
      addResult(section, {
        success: true,
        message: `Using market ID as string: ${marketIdString}`,
        data: { marketId: marketIdString },
        timestamp: Date.now(),
      });

      // Store orderId from placeOrder for cancelOrder test
      let placedOrderId: string | null = null;

      // Test placeOrder (submit order) - requires marketId
      if (currentMarketId) {
        // Prepare order request body
        // Leverage needs to be converted to satoshi format (following working example from TradeOperation.tsx)
        const leverage = 1.0; // Default leverage
        const leverageStr = toSatoshi(leverage.toString());
        const orderRequest = {
          marketId: marketIdString,
          side: "buy" as const,
          orderType: "LIMIT" as const,
          price: 40010.8778, // Price as decimal (will be converted to satoshi)
          quantity: 0.01, // Quantity as decimal (will be converted to satoshi)
          timeInForce: "GTC" as const,
          leverage: leverageStr, // Leverage in satoshi format (e.g., "100000000" for 1.0)
        };

        addResult(section, {
          success: true,
          message: `Testing placeOrder() (submit order) for market ${currentMarketId}...`,
          data: {
            requestBody: orderRequest,
            endpoint: "POST /clob/v1/order",
            note: "Request body (price, quantity, leverage) will be converted to satoshi format by SDK. Leverage is already in satoshi format.",
            leverage: {
              decimal: leverage,
              satoshi: leverageStr,
            },
          },
          timestamp: Date.now(),
        });
        try {
          // Using market ID from page
          // Place a limit buy order with small quantity for testing
          const orderResult =
            await exchangeClientRef.current.placeOrder(orderRequest);

          // Extract orderId from response (structure may vary)
          if (orderResult) {
            placedOrderId =
              orderResult.orderId ||
              orderResult.order_id ||
              orderResult.id ||
              null;
            if (placedOrderId) {
              addResult(section, {
                success: true,
                message: `placeOrder() - POST /clob/v1/order executed successfully. Order ID: ${placedOrderId.substring(0, 16)}...`,
                data: {
                  orderResult,
                  orderId: placedOrderId,
                  requestBody: orderRequest,
                  endpoint: "POST /clob/v1/order",
                },
                timestamp: Date.now(),
              });
            } else {
              addResult(section, {
                success: true,
                message:
                  "placeOrder() - POST /clob/v1/order executed successfully (orderId not found in response)",
                data: {
                  orderResult,
                  requestBody: orderRequest,
                  endpoint: "POST /clob/v1/order",
                },
                timestamp: Date.now(),
              });
            }
          } else {
            addResult(section, {
              success: false,
              message:
                "placeOrder() - POST /clob/v1/order returned empty response",
              data: {
                requestBody: orderRequest,
                endpoint: "POST /clob/v1/order",
              },
              timestamp: Date.now(),
            });
          }
        } catch (error: any) {
          addResult(section, {
            success: false,
            message: "placeOrder() - POST /clob/v1/order failed",
            error: error.message,
            data: {
              requestBody: orderRequest,
              endpoint: "POST /clob/v1/order",
            },
            timestamp: Date.now(),
          });
        }
      } else {
        addResult(section, {
          success: true,
          message: "Skipping placeOrder() test: No market ID available",
          timestamp: Date.now(),
        });
      }

      // Test cancelOrder - requires orderId and marketId
      if (currentMarketId && placedOrderId) {
        addResult(section, {
          success: true,
          message: `Testing cancelOrder() for order ${placedOrderId.substring(0, 16)}...`,
          timestamp: Date.now(),
        });
        try {
          // Using market ID and orderId from placeOrder
          const cancelResult = await exchangeClientRef.current.cancelOrder({
            orderId: placedOrderId,
            marketId: marketIdString,
          });
          addResult(section, {
            success: !!cancelResult,
            message: "cancelOrder() - POST /router/v1/tx executed successfully",
            data: cancelResult,
            timestamp: Date.now(),
          });
        } catch (error: any) {
          addResult(section, {
            success: false,
            message: "cancelOrder() - POST /router/v1/tx failed",
            error: error.message,
            timestamp: Date.now(),
          });
        }
      } else {
        if (!currentMarketId) {
          addResult(section, {
            success: true,
            message: "Skipping cancelOrder() test: No market ID available",
            timestamp: Date.now(),
          });
        } else if (!placedOrderId) {
          addResult(section, {
            success: true,
            message:
              "Skipping cancelOrder() test: No order ID available (placeOrder may have failed or orderId not in response)",
            timestamp: Date.now(),
          });
        }
      }

      // Test closePosition (requires marketId)
      if (currentMarketId) {
        addResult(section, {
          success: true,
          message: `Testing closePosition() for market ${currentMarketId}...`,
          timestamp: Date.now(),
        });
        try {
          // Using market ID from page
          const closeResult = await exchangeClientRef.current.closePosition({
            marketId: marketIdString,
            // quantity is optional - will close entire position if not provided
          });
          addResult(section, {
            success: !!closeResult,
            message:
              "closePosition() - POST /router/v1/tx executed successfully",
            data: closeResult,
            timestamp: Date.now(),
          });
        } catch (error: any) {
          addResult(section, {
            success: false,
            message:
              "closePosition() - POST /router/v1/tx failed (may not have open position)",
            error: error.message,
            timestamp: Date.now(),
          });
        }
      } else {
        addResult(section, {
          success: true,
          message: "Skipping closePosition() test: No market ID available",
          timestamp: Date.now(),
        });
      }

      // Test bucket operations - need to get buckets first
      addResult(section, {
        success: true,
        message: "Fetching buckets for bucket operation tests...",
        timestamp: Date.now(),
      });

      // Get buckets using InfoClient
      let availableBuckets: any[] = [];
      try {
        // Recreate InfoClient if needed
        if (!infoClientRef.current) {
          infoClientRef.current = new InfoClient({
            baseURL: baseURLRef.current,
          });
        }
        if (sessionToken && infoClientRef.current) {
          infoClientRef.current.setSessionToken(sessionToken);
        }

        const buckets = await infoClientRef.current.getBuckets(walletAddr);
        availableBuckets = Array.isArray(buckets) ? buckets : [];

        addResult(section, {
          success: true,
          message: `Found ${availableBuckets.length} bucket(s) for testing`,
          data: {
            bucketCount: availableBuckets.length,
            buckets: availableBuckets.slice(0, 3).map((b: any) => ({
              bucketId: b.bucket_id || b.id || b.bucketId,
              availableMargin: b.available_margin || b.availableMargin,
            })),
          },
          timestamp: Date.now(),
        });
      } catch (error: any) {
        addResult(section, {
          success: false,
          message: "Failed to fetch buckets for testing",
          error: error.message,
          timestamp: Date.now(),
        });
      }

      // Test extractToBucket (bank → bucket)
      if (availableBuckets.length > 0) {
        const testBucket = availableBuckets[0];
        const testBucketId =
          testBucket.bucket_id || testBucket.id || testBucket.bucketId;

        if (testBucketId) {
          addResult(section, {
            success: true,
            message: `Testing extractToBucket() (1 USD = 100000000 satoshi)...`,
            timestamp: Date.now(),
          });
          try {
            const extractResult =
              await exchangeClientRef.current.extractToBucket({
                bucketId: testBucketId,
                assetId: "USDC",
                amount: "100000000", // 1 USD in satoshi
              });
            addResult(section, {
              success: !!extractResult,
              message:
                "extractToBucket() - POST /bank/v1/extract-to-bucket executed successfully",
              data: extractResult,
              timestamp: Date.now(),
            });
          } catch (error: any) {
            addResult(section, {
              success: false,
              message:
                "extractToBucket() - POST /bank/v1/extract-to-bucket failed",
              error: error.message,
              timestamp: Date.now(),
            });
          }
        } else {
          addResult(section, {
            success: true,
            message:
              "Skipping extractToBucket() test: No valid bucket ID found",
            timestamp: Date.now(),
          });
        }
      } else {
        addResult(section, {
          success: true,
          message: "Skipping extractToBucket() test: No buckets available",
          timestamp: Date.now(),
        });
      }

      // Test withdrawFromBucket (bucket → bank)
      if (availableBuckets.length > 0) {
        const testBucket = availableBuckets[0];
        const testBucketId =
          testBucket.bucket_id || testBucket.id || testBucket.bucketId;

        if (testBucketId) {
          addResult(section, {
            success: true,
            message: `Testing withdrawFromBucket() (1 USD = 100000000 satoshi)...`,
            timestamp: Date.now(),
          });
          try {
            const withdrawResult =
              await exchangeClientRef.current.withdrawFromBucket({
                bucketId: testBucketId,
                assetId: "USDC",
                amount: "100000000", // 1 USD in satoshi
              });
            addResult(section, {
              success: !!withdrawResult,
              message:
                "withdrawFromBucket() - POST /bank/v1/withdraw-from-bucket executed successfully",
              data: withdrawResult,
              timestamp: Date.now(),
            });
          } catch (error: any) {
            addResult(section, {
              success: false,
              message:
                "withdrawFromBucket() - POST /bank/v1/withdraw-from-bucket failed",
              error: error.message,
              timestamp: Date.now(),
            });
          }
        } else {
          addResult(section, {
            success: true,
            message:
              "Skipping withdrawFromBucket() test: No valid bucket ID found",
            timestamp: Date.now(),
          });
        }
      } else {
        addResult(section, {
          success: true,
          message: "Skipping withdrawFromBucket() test: No buckets available",
          timestamp: Date.now(),
        });
      }

      // Test bucketTransfer (bucket → bucket)
      if (availableBuckets.length >= 2) {
        const sourceBucket = availableBuckets[0];
        const targetBucket = availableBuckets[1];
        const sourceBucketId =
          sourceBucket.bucket_id || sourceBucket.id || sourceBucket.bucketId;
        const targetBucketId =
          targetBucket.bucket_id || targetBucket.id || targetBucket.bucketId;

        if (sourceBucketId && targetBucketId) {
          addResult(section, {
            success: true,
            message: `Testing bucketTransfer() (1 USD = 100000000 satoshi)...`,
            timestamp: Date.now(),
          });
          try {
            const transferResult =
              await exchangeClientRef.current.bucketTransfer({
                fromBucketId: sourceBucketId,
                toBucketId: targetBucketId,
                assetId: "USDC",
                amount: "100000000", // 1 USD in satoshi
              });
            addResult(section, {
              success: !!transferResult,
              message:
                "bucketTransfer() - POST /bucket/v1/bucket/transfer executed successfully",
              data: transferResult,
              timestamp: Date.now(),
            });
          } catch (error: any) {
            addResult(section, {
              success: false,
              message:
                "bucketTransfer() - POST /bucket/v1/bucket/transfer failed",
              error: error.message,
              timestamp: Date.now(),
            });
          }
        } else {
          addResult(section, {
            success: true,
            message: "Skipping bucketTransfer() test: Invalid bucket IDs",
            timestamp: Date.now(),
          });
        }
      } else {
        addResult(section, {
          success: true,
          message:
            "Skipping bucketTransfer() test: Need at least 2 buckets (found " +
            availableBuckets.length +
            ")",
          timestamp: Date.now(),
        });
      }

      // Summary
      addResult(section, {
        success: true,
        message: "ExchangeClient tests completed",
        data: {
          walletAddress: walletAddr,
          marketId: marketIdString,
          sessionToken: sessionToken ? "present" : "not set",
          authToken: authToken ? "present" : "not set",
          methodsTested: [
            "placeOrder",
            "cancelOrder",
            "closePosition",
            "extractToBucket",
            "withdrawFromBucket",
            "bucketTransfer",
          ],
        },
        timestamp: Date.now(),
      });
    } catch (error: any) {
      addResult(section, {
        success: false,
        message: "ExchangeClient test error",
        error: error.message,
        timestamp: Date.now(),
      });
    }
  };

  // Run all tests
  const runAllTests = async () => {
    // Prevent concurrent test runs
    if (isRunning) {
      return;
    }

    setIsRunning(true);
    clearResults();
    toast.info("Running tests", {
      description: "Starting comprehensive SDK test suite...",
    });

    try {
      // Test utilities (synchronous)
      testUtilities();

      // Test InfoClient
      await testInfoClient();

      // Test Authentication (onboarding)
      await testAuthentication("Authentication");

      // Test Wallet Specific endpoints
      await testWalletSpecific();

      // Test ExchangeClient (if mnemonic provided)
      if (mnemonic) {
        await testExchangeClient();
      }

      // Completion toast will be shown by useEffect when isRunning becomes false
    } catch (error: any) {
      addResult("General", {
        success: false,
        message: "Test suite error",
        error: error.message,
        timestamp: Date.now(),
      });
      toast.error("Test suite error", {
        description:
          error.message || "An unexpected error occurred during testing.",
        duration: 6000,
      });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="App-content-row">
      <h1>SDK Test Page</h1>
      <p>Comprehensive test suite for all SDK APIs</p>

      {/* Configuration */}
      <div className="App-section">
        <h2>Configuration</h2>
        <div className="sdk-test-config-grid">
          <div>
            <label>
              HTTP Base URL:
              <input
                type="text"
                value={baseURL}
                onChange={(e) => setBaseURL(e.target.value)}
                className="sdk-test-config-input"
              />
            </label>
          </div>
          <div>
            <label>
              Market:
              <select
                value={marketId || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val) {
                    setMarketId(val); // Store as string
                  }
                }}
                className="sdk-test-config-select"
              >
                {markets.length === 0 ? (
                  <option value="">Loading markets...</option>
                ) : (
                  <>
                    <option value="" disabled>
                      -- Select a market --
                    </option>
                    {markets.map((market) => {
                      // Use exact market_id (string) from API, fallback to id converted to string
                      const marketIdValue =
                        market.market_id ?? market.id?.toString() ?? "";
                      // Prioritize moniker (from API), then symbol, then fallback
                      const displayName = market.moniker
                        ? market.moniker
                        : market.symbol
                          ? market.symbol
                          : market.baseAsset && market.quoteAsset
                            ? `${market.baseAsset}-${market.quoteAsset}`
                            : `Market ID: ${marketIdValue}`;
                      const isActive = market.active ? " [ACTIVE]" : "";
                      // Show truncated ID to avoid UI overflow
                      const idDisplay =
                        marketIdValue.length > 8
                          ? `${marketIdValue.substring(0, 8)}...`
                          : marketIdValue;
                      return (
                        <option key={marketIdValue} value={marketIdValue}>
                          {displayName} - ID: {idDisplay}
                          {isActive}
                        </option>
                      );
                    })}
                  </>
                )}
              </select>
              {markets.length > 0 && (
                <small
                  className={`sdk-test-config-hint ${
                    marketId === defaultMarketId ? "info" : "warning"
                  }`}
                >
                  {marketId === defaultMarketId
                    ? `✓ Auto-selected default market (ID: ${defaultMarketId?.substring(0, 16)}...)`
                    : `⚠ Manual selection (ID: ${marketId?.substring(0, 16)}...)`}
                </small>
              )}
            </label>
          </div>
          <div>
            <label>
              Mnemonic (for ExchangeClient):
              <input
                type="text"
                value={mnemonic || ""}
                onChange={(e) => setOnboardMnemonic(e.target.value)}
                placeholder="Optional"
                className="sdk-test-config-input"
              />
            </label>
          </div>
          <div>
            <label>
              Session Token (for authenticated InfoClient):
              <input
                type="text"
                value={sessionToken}
                onChange={(e) => setSessionToken(e.target.value)}
                disabled={!!(sessionToken && sessionToken.trim() !== "")}
                placeholder="Optional"
                className="sdk-test-config-input"
              />
            </label>
          </div>
          <div>
            <label>
              Auth Token:
              <input
                type="text"
                value={authToken}
                onChange={(e) => setAuthToken(e.target.value)}
                disabled={
                  !!(onboardAuthToken && onboardAuthToken.trim() !== "")
                }
                placeholder={
                  onboardAuthToken
                    ? "Auto-filled from onboarding"
                    : 'Optional - Use "Onboard" button to generate'
                }
                className="sdk-test-config-input"
              />
              {onboardAuthToken && (
                <small className="sdk-test-config-hint success">
                  ✓ Auto-filled from onboarding
                </small>
              )}
            </label>
          </div>
          <div>
            <label>
              Wallet Address:
              <input
                type="text"
                value={walletAddress || ""}
                disabled={!!(mnemonic && mnemonic.trim() !== "")}
                onChange={() => {
                  // Wallet address is managed by useOnboard, but allow manual override via setAccountData if needed
                  // For now, just show it as read-only when mnemonic is set
                }}
                placeholder="automatic (from mnemonic)"
                className="sdk-test-config-input"
              />
            </label>
          </div>
          <div>
            <label>
              Device ID:
              <input
                type="text"
                value={deviceID}
                onChange={(e) => setDeviceID(e.target.value)}
                className="sdk-test-config-input"
              />
            </label>
          </div>
          <div className="control-box">
            <RunAllTestsButton isRunning={isRunning} onClick={runAllTests} />
            <OnboardButton
              mnemonic={mnemonic || ""}
              baseURL={baseURL}
              onboardLoading={onboardLoading}
              isRunning={isRunning}
              onOnboard={(mnemonic, baseURL) => {
                if (mnemonic && mnemonic.trim() !== "") {
                  performOnboard(mnemonic, baseURL);
                } else {
                  toast.warning("Mnemonic required", {
                    description:
                      "Please enter a mnemonic first to perform onboard.",
                  });
                }
              }}
            />
            <ClearResultsButton isRunning={isRunning} onClick={clearResults} />
          </div>
        </div>

        {onboardAuthToken && (
          <span className="sdk-test-status success">
            ✓ Auth Token: {onboardAuthToken.substring(0, 20)}...
          </span>
        )}
        {onboardError && (
          <span className="sdk-test-status error">
            ❌ Onboard Error: {onboardError}
            <DismissErrorButton onClick={clearOnboardError} />
          </span>
        )}
      </div>

      {/* Results */}
      <div className="sdk-test-results">
        {Object.entries(sections).map(([sectionName, section]) => {
          const isExpanded = expandedSections.has(sectionName);
          return (
            <div key={sectionName} className="sdk-test-section">
              <SectionHeader
                sectionName={sectionName}
                isExpanded={isExpanded}
                onToggle={() => toggleSection(sectionName)}
              />
              {isExpanded && (
                <div className="sdk-test-section-content">
                  {section.results.map((result, index) => (
                    <TestResultItem key={index} result={result} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {Object.keys(sections).length === 0 && !isRunning && (
        <div className="sdk-test-empty-state">
          Click "Run All Tests" to start testing the SDK APIs
        </div>
      )}
    </div>
  );
}
